// CSR-2287/CSR-2250 — controlled, state-only "Policies on this Certificate"
// picker. Everything about WHICH rows are selectable/locked and WHAT the
// summary/warning copy says lives in policySelection.ts; this file is layout
// and a11y only (no trackEvent, no fetch, no flag check — phase 6 owns those).
import { useRef } from "react";
import type { Ref } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/formatters";
import type { Policy } from "@shared/schema";
import { PolicyStatusBadge } from "../policies/PolicyStatusBadge";
import {
  classifyCoiPickerPolicy,
  coiPolicyRowWarning,
  COI_PICKER_HELPER_COPY,
  COI_PICKER_TITLE,
  defaultCoiPolicySelection,
  orderCoiPickerPolicies,
  summarizeCoiPolicySelection,
  toggleCoiPolicySelection,
} from "./policySelection";

export interface PoliciesOnCertificatePickerChange {
  action: "check" | "uncheck" | "reset";
  status?: string | null;
}

interface Props {
  policies: Policy[];
  selectedIds: string[];
  onSelectionChange: (
    next: string[],
    change: PoliciesOnCertificatePickerChange,
  ) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  today?: Date;
  excludeAor?: boolean;
  // So phase 6 can focus the empty error after a blocked Preview/Send.
  emptyErrorRef?: Ref<HTMLElement>;
}

const ACCORDION_VALUE = "policies-on-certificate";

export function PoliciesOnCertificatePicker({
  policies,
  selectedIds,
  onSelectionChange,
  open,
  onOpenChange,
  today,
  excludeAor = false,
  emptyErrorRef,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const options = { excludeAor };
  const summary = summarizeCoiPolicySelection(policies, selectedIds, options);
  const ordered = orderCoiPickerPolicies(policies, today, options);

  const handleToggle = (
    policyId: string,
    checked: boolean,
    status: string | null | undefined,
  ) => {
    onSelectionChange(
      toggleCoiPolicySelection(selectedIds, policyId, checked),
      {
        action: checked ? "check" : "uncheck",
        status,
      },
    );
  };

  const handleReset = () => {
    onSelectionChange(defaultCoiPolicySelection(policies, options), {
      action: "reset",
    });
    // The Reset button unmounts once the selection is back to default, so send
    // focus to the trigger (the nearest control that survives the re-render)
    // rather than let it fall out of the block. CSR-2287.
    triggerRef.current?.focus();
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={open ? ACCORDION_VALUE : ""}
      onValueChange={(value) => onOpenChange(value === ACCORDION_VALUE)}
      data-testid="coi-policy-picker"
    >
      <AccordionItem value={ACCORDION_VALUE} className="rounded-lg border px-4">
        <AccordionTrigger
          ref={triggerRef}
          className="py-3 hover:no-underline"
          data-testid="coi-policy-picker-trigger"
        >
          <div className="flex flex-col items-start gap-1">
            <span className="font-semibold">{COI_PICKER_TITLE}</span>
            <span
              aria-live="polite"
              data-testid="coi-policy-picker-summary"
              className={cn(
                "text-sm font-normal",
                summary.summaryTone === "empty"
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {summary.summaryText}
            </span>
            {summary.headerWarningText && (
              <span
                className="flex items-center gap-1.5 text-xs font-normal text-amber-600 dark:text-amber-500"
                data-testid="coi-policy-picker-header-warning"
              >
                <AlertTriangle
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                {summary.headerWarningText}
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-t pt-3">
          <div className="mb-3 flex items-start justify-between gap-4">
            <p className="max-w-md text-xs text-muted-foreground">
              {COI_PICKER_HELPER_COPY}
            </p>
            {!summary.isDefault && (
              <button
                type="button"
                onClick={handleReset}
                className="shrink-0 text-xs font-medium text-foreground hover:underline"
                data-testid="coi-policy-picker-reset"
              >
                Reset to default
              </button>
            )}
          </div>
          {summary.emptyErrorText && (
            <div
              ref={emptyErrorRef as Ref<HTMLDivElement>}
              role="alert"
              // Programmatic focus target only: a div is unfocusable without
              // it, so the blocked-send focus move would be a silent no-op.
              // CSR-2287.
              tabIndex={-1}
              className="mb-2 text-xs text-destructive"
              data-testid="coi-policy-picker-empty-error"
            >
              {summary.emptyErrorText}
            </div>
          )}
          <div className="max-h-72 space-y-3 overflow-y-auto">
            {ordered.map((policy) => (
              <PolicyRow
                key={policy.id}
                policy={policy}
                checked={selectedIds.includes(policy.id)}
                today={today}
                excludeAor={excludeAor}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

interface PolicyRowProps {
  policy: Policy;
  checked: boolean;
  today: Date | undefined;
  excludeAor: boolean;
  onToggle: (
    policyId: string,
    checked: boolean,
    status: string | null | undefined,
  ) => void;
}

function PolicyRow({
  policy,
  checked,
  today,
  excludeAor,
  onToggle,
}: PolicyRowProps) {
  const { kind, lockedReason } = classifyCoiPickerPolicy(policy, today, {
    excludeAor,
  });
  const disabled = kind === "locked";
  const rowWarning = coiPolicyRowWarning(policy);
  const identityId = `coi-policy-identity-${policy.id}`;
  const reasonId = `coi-policy-locked-reason-${policy.id}`;
  const warningId = `coi-policy-warning-${policy.id}`;
  // CSR-2287 — the policy number and status are siblings of the control, so
  // without this the row announces as an unnamed checkbox.
  const describedBy =
    [disabled && lockedReason ? reasonId : null, rowWarning ? warningId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const checkbox = (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) =>
        onToggle(policy.id, value === true, policy.status)
      }
      aria-labelledby={disabled ? undefined : identityId}
      aria-describedby={disabled ? undefined : describedBy}
      // A locked row's semantics live on the focusable wrapper below; this
      // disabled control is unreachable, so hide it or the row is announced
      // twice. CSR-2287.
      aria-hidden={disabled ? true : undefined}
      data-testid={`coi-policy-checkbox-${policy.id}`}
    />
  );

  return (
    <div
      className={cn("flex items-start gap-3", disabled && "opacity-60")}
      data-testid={`coi-policy-row-${policy.id}`}
    >
      {disabled ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Radix disables the checkbox button itself (unfocusable), so the
                  wrapper is the thing that keeps this row reachable by Tab;
                  the sr-only span below (not the portalled tooltip content,
                  which mounts only on hover/focus) is what aria-describedby
                  actually points at. CSR-2287. */}
              <span
                tabIndex={0}
                role="checkbox"
                aria-checked={false}
                aria-disabled={true}
                aria-labelledby={identityId}
                aria-describedby={describedBy}
                className="mt-0.5 inline-flex cursor-default"
              >
                {checkbox}
              </span>
            </TooltipTrigger>
            <TooltipContent>{lockedReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        checkbox
      )}
      {disabled && lockedReason && (
        <span id={reasonId} className="sr-only">
          {lockedReason}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div id={identityId} className="flex flex-wrap items-center gap-2">
          <span
            className="max-w-[12rem] truncate text-sm font-medium"
            title={policy.policyNumber}
          >
            {policy.policyNumber}
          </span>
          <PolicyStatusBadge status={policy.status} />
        </div>
        <p className="text-xs text-muted-foreground">
          {policy.type || "—"} · {policy.carrier} ·{" "}
          {formatDate(policy.effectiveDate)} –{" "}
          {formatDate(policy.expirationDate)}
        </p>
        {rowWarning && (
          <p
            id={warningId}
            className="mt-0.5 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            {rowWarning}
          </p>
        )}
      </div>
    </div>
  );
}
